import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ME_QUERY_KEY } from "./auth-store.js";
import {
  beginLogin,
  BeginLoginError,
  fetchMe,
  type BeginLoginOptions,
  type SessionClaims,
} from "./session-api.js";
import {
  electSignInLeader,
  onSignInNews,
  releaseSignInLease,
  signInLeaseHeldElsewhere,
  takeSignInLease,
} from "./sign-in-lease.js";

/** The longest the page waits on the server's `Retry-After` before its one
 *  automatic retry (PB-5461). A longer wait is still offered as Try again. */
export const START_RETRY_CAP_SECONDS = 60;

/** How long the one automatic retry waits for a given `Retry-After`. */
export function startRetryDelayMs(retryAfterSeconds: number): number {
  return (
    Math.min(Math.max(retryAfterSeconds, 0), START_RETRY_CAP_SECONDS) * 1_000
  );
}

/** How often a tab waiting on another tab's sign-in asks /me again (d21). */
export const SIGN_IN_RECHECK_MS = 3_000;

/**
 * Why a sign-in could not be started, in the two cases the screen tells apart.
 * `busy` is the rate limiter (429); `retryAt` is when the automatic retry
 * fires, or null when there is none left. Everything else — a 5xx, a dropped
 * connection, an unreadable answer — is `not-responding`. Raw statuses and
 * error strings never reach the screen.
 */
export type SignInStartFailure =
  | { readonly kind: "busy"; readonly retryAt: number | null }
  | { readonly kind: "not-responding" };

export interface SignInStart {
  /** The last start failed and has not been tried again yet. */
  readonly failure: SignInStartFailure | null;
  /** Another tab is signing in, and this one waits for its session (d21). */
  readonly waiting: boolean;
  /** Start a sign-in the person asked for in THIS tab: it takes the sign-in
   *  lease, whoever holds it, and leaves for Keycloak when the BFF answers. */
  readonly start: (path: string, options?: BeginLoginOptions) => void;
  /** Start the sign-in a page needs by itself. Only the tab elected to lead
   *  goes to Keycloak; any other tab waits instead (d21). */
  readonly startAutomatic: (path: string) => void;
  /** Try the last start again now (the Try again button), in this tab. */
  readonly retry: () => void;
  /** The session arrived: whatever this tab was waiting for is over. */
  readonly stopWaiting: () => void;
}

type StartMode = "explicit" | "automatic";

/**
 * Starting a sign-in from a page that stays put when it cannot: the page guard
 * and `/auth/restart`. A refused start shows plain words and Try again, and a
 * start the server said to retry later (429 with `Retry-After`) is retried ONCE
 * by itself after that wait, capped at {@link START_RETRY_CAP_SECONDS}. One,
 * never more, so a busy server is never answered with a loop.
 *
 * An automatic start first elects one leader tab (PB-5461, d21); a tab that
 * loses reports `waiting` and starts nothing. Its one automatic retry elects
 * again, so a tab another tab took over from never goes back on its own.
 */
export function useSignInStart(): SignInStart {
  const [failure, setFailure] = useState<SignInStartFailure | null>(null);
  const [waiting, setWaiting] = useState(false);
  const lastRef = useRef<{
    path: string;
    options: BeginLoginOptions | undefined;
  } | null>(null);
  const inFlightRef = useRef(false);
  const autoRetryUsedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);

  const clearTimer = (): void => {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimer();
    };
  }, []);

  const run = useCallback(
    (
      path: string,
      options: BeginLoginOptions | undefined,
      mode: StartMode,
    ): void => {
      clearTimer();
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      lastRef.current = { path, options };
      setFailure(null);

      const begin = (): void => {
        setWaiting(false);
        (options ? beginLogin(path, options) : beginLogin(path)).then(
          (url) => {
            // Stays "in flight" on purpose: the browser is leaving.
            window.location.replace(url);
          },
          (error: unknown) => {
            inFlightRef.current = false;
            if (!mountedRef.current) return;
            const busy =
              error instanceof BeginLoginError && error.status === 429;
            const retryAfter = busy ? error.retryAfterSeconds : null;
            if (retryAfter !== null && !autoRetryUsedRef.current) {
              autoRetryUsedRef.current = true;
              const delay = startRetryDelayMs(retryAfter);
              timerRef.current = setTimeout(() => {
                timerRef.current = null;
                run(path, options, mode);
              }, delay);
              setFailure({ kind: "busy", retryAt: Date.now() + delay });
              return;
            }
            setFailure(
              busy
                ? { kind: "busy", retryAt: null }
                : { kind: "not-responding" },
            );
          },
        );
      };

      if (mode === "explicit") {
        // The lease is this tab's before the browser can leave for Keycloak.
        void takeSignInLease().then(begin);
        return;
      }
      void electSignInLeader().then((leads) => {
        if (!mountedRef.current) {
          inFlightRef.current = false;
          // Nobody is left here to sign in, so nobody may wait on this tab.
          if (leads) releaseSignInLease();
          return;
        }
        if (leads) {
          begin();
          return;
        }
        inFlightRef.current = false;
        setWaiting(true);
      });
    },
    [],
  );

  const start = useCallback(
    (path: string, options?: BeginLoginOptions): void =>
      run(path, options, "explicit"),
    [run],
  );

  const startAutomatic = useCallback(
    (path: string): void => run(path, undefined, "automatic"),
    [run],
  );

  const retry = useCallback((): void => {
    const last = lastRef.current;
    if (last) start(last.path, last.options);
  }, [start]);

  const stopWaiting = useCallback((): void => setWaiting(false), []);

  return { failure, waiting, start, startAutomatic, retry, stopWaiting };
}

/** What a fresh /me says, or `unknown` when it gave no verdict. */
async function askForSession(
  queryClient: QueryClient,
): Promise<"signed-in" | "signed-out" | "unknown"> {
  try {
    // Drop a request already on its way: it may have left before the other
    // tab's session existed, and its "no" would be stale.
    await queryClient.cancelQueries({ queryKey: ME_QUERY_KEY, exact: true });
    const me = await queryClient.fetchQuery<SessionClaims | null>({
      queryKey: ME_QUERY_KEY,
      queryFn: fetchMe,
      staleTime: 0,
      // The poll is its own retry.
      retry: false,
    });
    return me ? "signed-in" : "signed-out";
  } catch {
    return "unknown";
  }
}

/**
 * While mounted, wait for another tab's sign-in (PB-5461, d21). /me is asked
 * again when the lease changes, when a "signed-in" announcement arrives, when
 * the window gets focus back, and every {@link SIGN_IN_RECHECK_MS}:
 *   - a session -> `onSignedIn`;
 *   - no session, and no other tab holding a live lease -> `onLeaseFree`, so
 *     this tab tries to lead the sign-in itself.
 * A /me that gives no verdict does neither: a failure is not a sign-out
 * (PB-4900), so it never starts a sign-in.
 *
 * Fetching /me writes its answer into the shared query, so the page guard sees
 * the session the moment it arrives.
 */
export function useSignInElsewhere(handlers: {
  readonly onSignedIn: () => void;
  readonly onLeaseFree: () => void;
}): void {
  const queryClient = useQueryClient();
  const handlersRef = useRef(handlers);
  useEffect(() => {
    handlersRef.current = handlers;
  });

  useEffect(() => {
    let stopped = false;
    let running = false;
    let again = false;

    const recheck = async (): Promise<void> => {
      if (running) {
        // Never drop news: ask again once the current answer is in.
        again = true;
        return;
      }
      running = true;
      try {
        do {
          again = false;
          /* Read the lease BEFORE asking. The leader gives it up only once its
             session exists, so a free lease followed by a "no session" really
             means nobody is signing in, never a session about to appear. */
          const leaseFree = !signInLeaseHeldElsewhere();
          const verdict = await askForSession(queryClient);
          if (stopped) return;
          if (verdict === "signed-in") {
            handlersRef.current.onSignedIn();
            return;
          }
          if (!again && verdict === "signed-out" && leaseFree) {
            handlersRef.current.onLeaseFree();
            return;
          }
        } while (again);
      } finally {
        running = false;
      }
    };

    const stopNews = onSignInNews(() => void recheck());
    const poll = setInterval(() => void recheck(), SIGN_IN_RECHECK_MS);
    return () => {
      stopped = true;
      stopNews();
      clearInterval(poll);
    };
  }, [queryClient]);
}
